// verdictModel.js — the weight-free overall verdict.
//
// The overall verdict is NOT an average and uses NO invented weights. Each lens
// (Design / Execution / Read) gets a status — Solid / Soft / Broken — and the
// band is the most optimistic recommendation that none of the lens statuses veto:
//
//   Design = foundation gate · Read = outcome gate · Execution = delivery gate
//
//   Keep     all three Solid
//   Polish   no lens Broken, at least one Soft
//   Rework   exactly one lens Broken (a load-bearing lens isn't doing its job)
//   Rebuild  two or more Broken  (scene/sequence grain calls this "Low on its own" — those units
//            are graded in isolation, so the band cannot license a cut/merge; see bandLabel below)
//
// Numbers stay at the AXIS level (the drill-down), where granularity is earned.
// Status thresholds are a rubric cut (tunable), not a weighting.

// Band cut points follow the frozen spec (AXIS_INSPECTOR_SPEC §4.2 / der_syn_band): the bands are
// inclusive-LOW — solid is strictly ABOVE 6, soft is >4 through 6, broken is 4 and below. So the exact
// boundary scores land: 6.0 → soft (not solid), 4.0 → broken (not soft), 4.3 → soft. This matches PHP
// der_syn_band() and the E2 band-parity fixture (previously JS rounded these two boundaries up — the
// one place PHP↔JS disagreed).
const SOLID_MIN = 6;   // > 6   → Solid
const SOFT_MIN  = 4;   // >4–6  → Soft ; ≤ 4 → Broken

export function lensStatus(score) {
  if (score == null || isNaN(score)) return null;
  if (score > SOLID_MIN) return 'solid';
  if (score > SOFT_MIN)  return 'soft';
  return 'broken';
}

// status visual + word
export const STATUS_META = {
  solid:  { label: 'Solid',  fg: '#1f7a37', bg: '#e3f3e6', bd: '#bfe3c7' },
  soft:   { label: 'Soft',   fg: '#8a5a12', bg: '#fbefdc', bd: '#f0dcb0' },
  broken: { label: 'Broken', fg: '#a3261e', bg: '#fde7e6', bd: '#f3c9c6' },
};

// one writer-facing meaning sentence per lens × status
export const LENS_MEANING = {
  Design: {
    solid:  'The story machine is built and mostly working.',
    soft:   'The structure works but is leaking power in places.',
    broken: 'The underlying story machine isn’t holding — fix this before polish.',
  },
  Execution: {
    solid:  'The scenes land cleanly on the page.',
    soft:   'The scenes work but need cleaner delivery.',
    broken: 'How it plays on the page is breaking down.',
  },
  Read: {
    solid:  'It grips and pulls forward.',
    soft:   'It is steady but not yet gripping.',
    broken: 'It isn’t carrying interest, tension, or feeling.',
  },
};
LENS_MEANING.Involvement = LENS_MEANING.Read;   // display name since 2026-10-14; 'Read' kept for stored data

const BAND_LABEL_SCRIPT = { keep: 'Keep', polish: 'Polish', rework: 'Rework', heavy: 'Rebuild',
                            partial: 'Not fully read' };
// Scene/sequence grain: the worst band used to read "Cut or combine", which is a BOUNDARY claim —
// and the grader that produced these numbers has never seen a neighbouring unit (it is handed one
// scene row, `run_stage0.php:810-816`). It says only that the unit reads low ON ITS OWN. See the
// `heavy` article in wikiContent.js and DER_SCENE_TRIAGE_AUDIT_v0_1.md.
const BAND_LABEL_SCENE  = { keep: 'Keep', polish: 'Polish', rework: 'Rework', heavy: 'Low on its own',
                            partial: 'Not fully read' };

// Pill styling for the same band — a light tint of each workflow hue (kept in
// lockstep with BAND_FILL so the wheel wedge, legend swatch, centre, and the
// header/node pills all read as one categorical language).
export const BAND_STYLE = {
  keep:   { fg: '#0a7a37', bg: '#e4f9ec', bd: '#b3ebc8' },  // green
  polish: { fg: '#8a6d00', bg: '#fff8d6', bd: '#f0e08f' },  // yellow/gold
  rework: { fg: '#b34c00', bg: '#fff0e2', bd: '#f8d0ab' },  // orange
  heavy:  { fg: '#b71c2c', bg: '#ffe5e9', bd: '#f6c2cb' },  // red
  // Deliberately OUTSIDE the good→bad hue run: "not enough read to call it" is not a position on
  // the scale, and colouring it anywhere on green→red would assert one.
  partial: { fg: '#5b7481', bg: '#eef2f5', bd: '#d3dee4' }, // slate
};

// statuses {design, execution, read} → band key (keep|polish|rework|heavy|partial)
//
// PARTIAL EVIDENCE — fixed 2026-08-13, landmine 76. This used to filter nulls and then decide, so a
// unit with only ONE graded lens returned a confident band. {design:'solid'} gave 0 broken, 0 soft
// → 'keep', painting vivid green on a two-thirds-ungraded unit.
//
// The gate is a VETO model, so the two directions are not symmetric:
//   • 'keep' and 'polish' are ABSENCE claims — they assert that NO lens is broken. A missing lens
//     is exactly the case where you cannot assert that. These now require all three lenses.
//   • 'rework' and 'heavy' rest on POSITIVE evidence — a broken lens you actually observed. Those
//     still stand on partial data; they are a floor (an unseen lens could only make it worse), and
//     understating heavy as rework errs toward the writer rather than toward the product.
// Anything else with some data but not enough returns 'partial', which is a real band key with its
// own neutral fill and label rather than a null that silently falls through to a score gradient.
export function gatedBand(statuses) {
  const keys = ['design', 'execution', 'read'];
  const vals = keys.map(k => statuses[k]).filter(Boolean);
  if (vals.length === 0) return null;            // nothing graded at all
  const broken = vals.filter(s => s === 'broken').length;
  const soft   = vals.filter(s => s === 'soft').length;

  if (broken >= 2) return 'heavy';               // positive evidence, holds on partial data
  if (broken === 1) return 'rework';             // positive evidence, floor

  // Everything below asserts the ABSENCE of a broken lens, so it needs the full read.
  if (vals.length < keys.length) return 'partial';

  if (soft >= 1) return 'polish';
  return 'keep';                                 // all three present, all solid
}

// Worst band differs by grain. At act/script grain the unit IS the whole read, so "Rebuild" is a
// claim the evidence supports. At scene/sequence grain it is not: the unit was graded in isolation,
// so all we can say is that it reads low on its own. Keep/Polish/Rework are shared.
export function bandLabel(band, grain) {
  const isolated = grain === 'scene' || grain === 'sequence';
  const map = isolated ? BAND_LABEL_SCENE : BAND_LABEL_SCRIPT;
  return map[band] || band;
}

// Triage band = green→red (good→needs-work), but in a VIVID / ELECTRIC tone — full
// saturation, so it stays clearly distinct from the pale, low-alpha red→amber→green
// GRADE gradient the axis/D&E metrics use. Same intuition (green good, red bad),
// different energy.
export const BAND_FILL = {
  keep:   '#22C55E',  // teal-green       — keep
  polish: '#EAB308',  // warm gold        — polish
  rework: '#F97316',  // strong orange    — rework
  heavy:  '#E11D48',  // deep raspberry   — cut / rebuild
  // Not a point on the good→bad run: the read is incomplete, so no position is claimed. Muted so it
  // recedes rather than competing with the four real verdicts.
  partial: '#CBD5DF',  // slate            — not enough read to call it
};

// lens scores → band key (keep|polish|rework|heavy), or null when no data.
export function bandOf(lenses) {
  return gatedBand({
    design: lensStatus(lenses.design),
    execution: lensStatus(lenses.execution),
    read: lensStatus(lenses.read),
  });
}

// One-sentence, weight-free explanation of WHY this band — derived from statuses.
export function bandWhy(statuses) {
  const named = s => ({ design: 'Design', execution: 'Execution', read: 'Involvement' }[s]);
  const broken = ['design', 'execution', 'read'].filter(k => statuses[k] === 'broken').map(named);
  const soft   = ['design', 'execution', 'read'].filter(k => statuses[k] === 'soft').map(named);
  const list = arr => arr.length === 1 ? arr[0]
    : arr.slice(0, -1).join(', ') + ' and ' + arr[arr.length - 1];
  if (broken.length >= 2) return `${list(broken)} are broken — the draft needs rebuilding before polish.`;
  if (broken.length === 1) return `${broken[0]} is broken — a load-bearing lens isn’t doing its job yet.`;

  // Mirrors the gate: below this point every sentence asserts that NO lens is broken, which is only
  // sayable with all three in hand. Without them, say what is missing instead of guessing.
  // (Landmine 76 — this used to fall through to "All three lenses are solid".)
  const missing = ['design', 'execution', 'read'].filter(k => !statuses[k]).map(named);
  if (missing.length) {
    return `${list(missing)} ${missing.length === 1 ? 'was' : 'were'} not scored here, so there isn’t enough to call this unit either way.`;
  }

  if (soft.length >= 1)  return `No lens is broken, but ${list(soft)} ${soft.length === 1 ? 'is' : 'are'} soft — this is a polish draft.`;
  return 'All three groups are solid — protect the draft and make targeted improvements.';
}

// Convenience: lens scores → {statuses, band, label, why}
export function verdictFromLenses({ design, execution, read }, grain) {
  const statuses = {
    design: lensStatus(design),
    execution: lensStatus(execution),
    read: lensStatus(read),
  };
  const band = gatedBand(statuses);
  return { statuses, band, label: band ? bandLabel(band, grain) : null, why: band ? bandWhy(statuses) : null };
}
